import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, EnvelopeSimple } from '@phosphor-icons/react';
import PRACTICE_AREAS from '../data/practiceAreas';
import TI from './brand/TI';

/* ====================================================================
 * <PracticeAreas /> — editorial 8-practice services section.
 *
 * Replaces the old flat 8-card ServiceCard grid with a richer,
 * information-architecture-driven layout:
 *
 *   - Each practice is a full-width card with header image
 *   - Bulleted service list
 *   - Cross-link chips: industries · regulations · ToxIntel capability
 *   - Availability badge: "Available" or "Expert-led consulting"
 *   - Contact-an-Expert CTA (mailto with pre-filled subject)
 *   - Optional "Learn more" link to a dedicated /practice/... page
 *
 * Cosmetics & Personal Care is called out as the practice's core
 * specialization. Medical Devices carries a positioning disclaimer.
 * ==================================================================== */

const SLATE = '#0B1426';
const IVORY = '#F5F1E8';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const INK = '#1F2937';

function AvailabilityBadge({ availability }) {
  const isExpertLed = availability === 'expert-led';
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] px-2.5 py-1 rounded-sm border"
      style={
        isExpertLed
          ? { borderColor: '#D6CDB8', color: MUTED, backgroundColor: 'rgba(255,255,255,0.5)' }
          : { borderColor: CORAL, color: CORAL, backgroundColor: 'rgba(255,78,106,0.06)' }
      }
      data-testid={`practice-availability-${availability}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: isExpertLed ? MUTED : CORAL }}
      />
      {isExpertLed ? 'Expert-led consulting' : 'Available'}
    </span>
  );
}

function Chip({ label, tone = 'default' }) {
  const styles =
    tone === 'toxintel'
      ? { borderColor: SLATE, color: SLATE, backgroundColor: 'rgba(11,20,38,0.04)' }
      : { borderColor: '#D6CDB8', color: MUTED, backgroundColor: 'transparent' };
  return (
    <span
      className="inline-flex items-center text-[11px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border mr-1.5 mb-1.5"
      style={styles}
    >
      {label}
    </span>
  );
}

function PracticeCard({ practice, coreSpecialization = false }) {
  const contactSubject = encodeURIComponent(
    `${practice.title} — Consulting inquiry`
  );
  const contactBody = encodeURIComponent(
    `Briefly: company, product category, jurisdictions, timeline, and the scientific question that prompted the call.\n\n(Interest area: ${practice.title})`
  );

  return (
    <article
      className="border rounded-sm overflow-hidden flex flex-col"
      style={{ borderColor: '#D6CDB8', backgroundColor: '#FBF7EE' }}
      data-testid={`practice-${practice.id}`}
      id={`practice-${practice.id}`}
    >
      {/* Header image */}
      {practice.image && (
        <div className="w-full aspect-[16/9] overflow-hidden border-b" style={{ borderColor: '#E3D9C0' }}>
          <img
            src={practice.image}
            alt={practice.imageAlt || ''}
            className="block w-full h-full object-cover select-none"
            loading="lazy"
          />
        </div>
      )}

      <div className="p-7 lg:p-8 flex flex-col grow">
        {/* Eyebrow row: order + availability */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p
            className="text-[10px] font-mono uppercase tracking-[0.3em]"
            style={{ color: CORAL }}
          >
            {practice.order} &middot; Practice
          </p>
          <AvailabilityBadge availability={practice.availability} />
        </div>

        {/* Title + core-specialization marker */}
        <div className="flex items-start gap-2 mb-2">
          <h3 className="font-serif text-2xl lg:text-[26px] leading-tight" style={{ color: SLATE }}>
            {practice.title}
          </h3>
        </div>
        {coreSpecialization && (
          <p
            className="text-[10px] font-mono uppercase tracking-[0.28em] mb-3"
            style={{ color: CORAL }}
            data-testid={`practice-core-marker-${practice.id}`}
          >
            Scientific foundation of the practice
          </p>
        )}

        <p className="text-[13.5px] leading-relaxed mb-4 italic" style={{ color: MUTED }}>
          {practice.tagline}
        </p>
        <p className="text-[14px] leading-relaxed mb-6" style={{ color: INK }}>
          {practice.body}
        </p>

        {/* Services list */}
        <ul
          className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-6 text-[13px]"
          style={{ color: INK }}
          data-testid={`practice-services-${practice.id}`}
        >
          {practice.services.map((s) => (
            <li key={s} className="flex items-start gap-2">
              <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: CORAL }} />
              <span>{s}</span>
            </li>
          ))}
        </ul>

        {/* Positioning disclaimer (Medical Devices only) */}
        {practice.positioningDisclaimer && (
          <div
            className="mb-6 p-4 rounded-sm border text-[12.5px] leading-relaxed"
            style={{ borderColor: '#D6CDB8', backgroundColor: 'rgba(255,255,255,0.55)', color: MUTED }}
            data-testid={`practice-disclaimer-${practice.id}`}
          >
            {practice.positioningDisclaimer}
          </div>
        )}

        {/* Cross-link chips */}
        <div className="mt-auto space-y-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] mb-2" style={{ color: MUTED }}>
              Industries
            </p>
            <div className="flex flex-wrap">
              {practice.industries.map((i) => (
                <Chip key={i} label={i} />
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] mb-2" style={{ color: MUTED }}>
              Applicable regulations
            </p>
            <div className="flex flex-wrap">
              {practice.regulations.map((r) => (
                <Chip key={r} label={r} />
              ))}
            </div>
          </div>
          {practice.toxintelCapability && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.28em] mb-2" style={{ color: MUTED }}>
                Related <TI /> capability
              </p>
              <div className="flex flex-wrap">
                <Chip label={practice.toxintelCapability} tone="toxintel" />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 border-t flex flex-wrap gap-3" style={{ borderColor: '#E3D9C0' }}>
            <a
              href={`mailto:regulatory@tagc.us?subject=${contactSubject}&body=${contactBody}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[12px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px"
              style={{ backgroundColor: SLATE, color: IVORY }}
              data-testid={`practice-contact-${practice.id}`}
            >
              <EnvelopeSimple size={13} weight="bold" /> Contact an expert
            </a>
            <a
              href={`mailto:regulatory@tagc.us?subject=${encodeURIComponent(
                `Request consultation — ${practice.title}`
              )}&body=${contactBody}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[12px] font-medium uppercase tracking-wider border transition-colors hover:bg-[#0B1426] hover:text-[#F5F1E8]"
              style={{ borderColor: SLATE, color: SLATE }}
              data-testid={`practice-request-${practice.id}`}
            >
              Request consultation
            </a>
            {practice.detailHref && (
              <Link
                to={practice.detailHref}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-[12px] font-medium uppercase tracking-wider transition-colors"
                style={{ color: CORAL }}
                data-testid={`practice-learn-more-${practice.id}`}
              >
                Learn more <ArrowUpRight size={13} weight="bold" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function PracticeAreas() {
  return (
    <section
      id="services"
      className="border-t"
      style={{ borderColor: '#E3D9C0' }}
      data-testid="tagc-practice-areas"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        {/* Section header */}
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-4">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
              style={{ color: CORAL }}
              data-testid="practice-eyebrow"
            >
              02 &mdash; Scientific practice
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]" style={{ color: SLATE }}>
              Eight scientific practice areas. One consistent standard of evidence.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
            <p className="text-base lg:text-lg leading-relaxed mb-4" style={{ color: MUTED }}>
              Aᴳ delivers scientific consulting across cosmetics, personal care, consumer
              products, pharmaceuticals, medical devices, industrial chemicals, and
              environmental toxicology. The same exposure-based methodology, board-certified
              oversight, and documentation discipline scales across every practice we engage.
            </p>
            <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>
              <TI /> is our enterprise scientific safety intelligence platform &mdash; a distinct
              product that operationalizes many of these workflows. It supports, but does not
              replace, the consulting practice.
            </p>
          </div>
        </div>

        {/* Practice cards — single column on all breakpoints for editorial density */}
        <div className="grid grid-cols-1 gap-10">
          {PRACTICE_AREAS.map((practice) => (
            <PracticeCard
              key={practice.id}
              practice={practice}
              coreSpecialization={practice.id === 'cosmetics-personal-care'}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
